import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { inboxKeys } from "./queryKeys";
import { getMessages, sendMessage } from "./api";
import { getChannel, sortMessagesAscending } from "@/components/inbox/utils";
import type { InboxConversation, InboxMessage } from "@/types/inbox";

// Optimistic messages that never reconcile are dropped after this long.
const OPTIMISTIC_TTL_MS = 10 * 60 * 1000;

function matchesOptimistic(real: InboxMessage, optimistic: InboxMessage): boolean {
	const contentMatch = (real.text || "").trim() === (optimistic.text || "").trim();
	const timeMatch =
		Math.abs(new Date(real.timestamp || 0).getTime() - new Date(optimistic.timestamp || 0).getTime()) <
		30000;
	return contentMatch && timeMatch;
}

/**
 * Merge freshly fetched messages with optimistic ones still in the cache:
 * an optimistic message survives refetches until its real counterpart
 * (same content, timestamp within 30s) shows up — the strategy the old
 * drawer used, applied at the query layer so realtime refreshes can never
 * clobber an in-flight send.
 */
export function mergeWithOptimistic(
	previous: InboxMessage[] | undefined,
	fresh: InboxMessage[]
): InboxMessage[] {
	const pendingOptimistic = (previous || []).filter(
		(msg) =>
			msg.optimistic &&
			Date.now() - new Date(msg.timestamp || 0).getTime() < OPTIMISTIC_TTL_MS &&
			!fresh.some((real) => matchesOptimistic(real, msg))
	);
	return sortMessagesAscending([...fresh, ...pendingOptimistic]);
}

/** Messages for the selected conversation, ascending, optimistic-aware. */
export function useMessages(conversation: InboxConversation | undefined) {
	const queryClient = useQueryClient();
	const conversationId = conversation?.id;

	return useQuery({
		queryKey: inboxKeys.messages(conversationId || "none"),
		enabled: !!conversationId,
		staleTime: 15 * 1000,
		queryFn: async () => {
			const fresh = await getMessages({
				id: conversationId!,
				source_account_id: conversation?.source_account_id,
				source_account_type: conversation?.source_account_type,
			});
			const previous = queryClient.getQueryData<InboxMessage[]>(
				inboxKeys.messages(conversationId!)
			);
			return mergeWithOptimistic(previous, fresh);
		},
	});
}

interface SendMessageContext {
	optimisticId: string;
}

/**
 * Optimistic send: the message appears instantly, is flagged as sent on
 * success, and reconciles against the real message via staggered refetches.
 * On failure the optimistic bubble is removed and the error propagates so
 * the composer can restore the user's draft.
 */
export function useSendMessage(conversation: InboxConversation | undefined) {
	const queryClient = useQueryClient();
	const conversationId = conversation?.id;
	const channel = getChannel(conversation);

	return useMutation<void, Error, string, SendMessageContext>({
		mutationFn: async (text: string) => {
			if (!conversationId) throw new Error("No conversation selected");
			await sendMessage({
				conversationId,
				message: text,
				conversationType: conversation?.conversation_type,
				isEmailConversation: channel === "email",
				accountId: conversation?.source_account_id,
				accountType: conversation?.source_account_type,
			});
		},
		onMutate: async (text: string) => {
			const key = inboxKeys.messages(conversationId!);
			await queryClient.cancelQueries({ queryKey: key });
			const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
			const optimisticMessage: InboxMessage = {
				id: optimisticId,
				text,
				timestamp: new Date().toISOString(),
				is_sender: 1,
				optimistic: true,
				sending: true,
			};
			queryClient.setQueryData<InboxMessage[]>(key, (previous) => [
				...(previous || []),
				optimisticMessage,
			]);
			return { optimisticId };
		},
		onError: (_error, _text, context) => {
			if (!context || !conversationId) return;
			queryClient.setQueryData<InboxMessage[]>(inboxKeys.messages(conversationId), (previous) =>
				(previous || []).filter((msg) => msg.id !== context.optimisticId)
			);
		},
		onSuccess: (_data, _text, context) => {
			if (!conversationId) return;
			const key = inboxKeys.messages(conversationId);
			queryClient.setQueryData<InboxMessage[]>(key, (previous) =>
				(previous || []).map((msg) =>
					msg.id === context?.optimisticId ? { ...msg, sending: false, sent: true } : msg
				)
			);
			// Staggered reconciliation refetches; mergeWithOptimistic swaps the
			// optimistic bubble for the real message once the backend has it.
			[1000, 3000, 7000].forEach((delay) => {
				setTimeout(() => {
					queryClient.invalidateQueries({ queryKey: key });
				}, delay);
			});
			queryClient.invalidateQueries({ queryKey: inboxKeys.conversations() });
		},
	});
}
